import React from "react";

import { useInvestmentsAccessQuery } from "@features/investments/queries/access";
import { useCurrentUserQuery } from "@features/investments/queries/current-user";

export function SwingAccountMenu({ context = "swing" }: { context?: "swing" | "checklist" } = {}): JSX.Element {
  const isChecklist = context === "checklist";
  const containerRef = React.useRef<HTMLDivElement | null>(null);
  const [open, setOpen] = React.useState(false);
  const [imageFailed, setImageFailed] = React.useState(false);
  const currentUserQuery = useCurrentUserQuery();
  const accessQuery = useInvestmentsAccessQuery();
  const currentUser = currentUserQuery.data?.authenticated ? currentUserQuery.data.user : undefined;
  const fallbackUser = accessQuery.data?.user;
  const displayName = currentUser?.name || fallbackUser?.name || "User";
  const displayEmail = currentUser?.email || fallbackUser?.email || "";
  const displayPicture = currentUser?.picture || "";
  const showImage = displayPicture !== "" && !imageFailed;

  React.useEffect(() => setImageFailed(false), [displayPicture]);

  React.useEffect(() => {
    if (!open) return undefined;
    const handlePointerDown = (event: MouseEvent): void => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) setOpen(false);
    };
    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [open]);

  function navigate(path: string): void {
    setOpen(false);
    window.location.hash = `#${path}`;
  }

  async function logout(): Promise<void> {
    setOpen(false);
    try {
      await fetch("/auth/logout", { method: "POST", credentials: "include" });
    } finally {
      window.location.href = "/#/";
    }
  }

  return (
    <div className="swing-account" ref={containerRef}>
      <button
        type="button"
        className="swing-account-trigger"
        aria-label={`${displayName} profile menu`}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
      >
        {showImage ? (
          <img className="swing-account-avatar" src={displayPicture} alt={displayName} referrerPolicy="no-referrer" onError={() => setImageFailed(true)} />
        ) : (
          <span className="swing-account-avatar swing-account-initials">{initials(displayName)}</span>
        )}
        <span className="swing-account-name">{displayName}</span>
        <span className={`swing-account-caret${open ? " open" : ""}`} aria-hidden="true">▾</span>
      </button>

      {open && (
        <div className="swing-account-menu" role="menu" aria-label={isChecklist ? "Trading Checklist account menu" : "Swing Trading account menu"}>
          <header>
            <strong>{displayName}</strong>
            {displayEmail && <span>{displayEmail}</span>}
            {!isChecklist && <em>Swing Trading</em>}
          </header>
          {isChecklist ? <>
            <button type="button" role="menuitem" onClick={() => navigate("/investments")}>Investments</button>
            <button type="button" role="menuitem" onClick={() => navigate("/swing-trading")}>Swing Trading</button>
          </> : <>
          <button type="button" role="menuitem" onClick={() => navigate("/swing-trading")}>Today</button>
          <button type="button" role="menuitem" onClick={() => navigate("/swing-trading/scanner")}>Momentum scanner</button>
          <button type="button" role="menuitem" onClick={() => navigate("/swing-trading/focus")}>Focus Desk</button>
          <button type="button" role="menuitem" onClick={() => navigate("/swing-trading/operator")}>Swing Operator</button>
          <div role="separator" />
          <button type="button" role="menuitem" onClick={() => navigate("/investments")}>Open Investments</button>
          <button type="button" role="menuitem" onClick={() => navigate("/trading-checklist")}>Trading Checklist</button>
          </>}
          {isChecklist && <div role="separator" />}
          <button type="button" role="menuitem" onClick={() => void logout()}>Logout</button>
        </div>
      )}
    </div>
  );
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean).slice(0, 2);
  return parts.length === 0 ? "U" : parts.map((part) => part[0]?.toUpperCase() || "").join("");
}
